import { ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { Check, ChevronDown } from 'lucide-react'
import { DateTime } from 'luxon'
import {
    TCapacityUnit,
    TPropertyUnit,
    customCapacityValue,
    customTimeframeDate,
    endDateForTimeframe,
    timeframeLabelForDate,
} from './propertyOptions'

export {
    CAPACITY_OPTIONS,
    CAPACITY_UNITS,
    NO_CAPACITY_LABEL,
    NO_TIMEFRAME_LABEL,
    TIMEFRAME_OPTIONS,
    capacityFromLabel,
} from './propertyOptions'

const CUSTOM_LABEL = 'Custom…'

interface PropertyPillProps {
    /** Optional leading glyph — pass `null` for the icon-less frequency pill. */
    icon: ReactNode
    value: string
    /** With `units`, these are the rows above the tabs — for capacity, just the "unset" sentinel. */
    options: string[]
    /** Popover edge alignment (ReviewScreen's frequency pill anchors `end`). */
    align?: 'start' | 'end'
    onSelect: (value: string) => void
    /** Reports open state up so a stepper can suppress Enter-to-advance mid-choose. */
    onOpenChange?: (open: boolean) => void
    /** Unit tabs, each with its own ladder and optional numeric entry. Omit for a plain canned list. */
    units?: TPropertyUnit[]
    /** Which unit `value` is written in, so the popover opens on the tab the current pick lives under. */
    unitKey?: TCapacityUnit
    /** Adds a Custom… native date entry. Kept separate from `units`, whose custom value is numeric. */
    customDate?: boolean
}

/**
 * A Badge-style trigger opening a popover of canned options - shared by ScaffoldFlow's step 3 and
 * ReviewScreen's property row (and per-cadence frequency picker). Reports open state up via the
 * optional `onOpenChange`.
 *
 * Pass `units` and it grows a unit tab strip and a Custom… numeric entry (the capacity pill needs
 * both: hours or sessions, and any number in between the rungs). Every path out of it still reports
 * a finished label through `onSelect`, so nothing downstream has to know which affordance produced it.
 */
const PropertyPill = ({
    icon,
    value,
    options,
    align = 'start',
    onSelect,
    onOpenChange,
    units,
    unitKey,
    customDate = false,
}: PropertyPillProps) => {
    const [open, setOpen] = useState(false)
    const [activeKey, setActiveKey] = useState<TCapacityUnit | undefined>(unitKey)
    const [custom, setCustom] = useState('')
    const [customOpen, setCustomOpen] = useState(false)

    const activeUnit = units?.find((unit) => unit.key === activeKey) ?? units?.[0]

    const changeOpen = (next: boolean) => {
        setOpen(next)
        onOpenChange?.(next)
        // Reopening always lands on the tab holding the current pick, with the numeric field put away.
        // Closing without committing — including Escape, routed here below — leaves `value` alone.
        if (!next) {
            setCustomOpen(false)
            setCustom('')
        } else {
            setActiveKey(unitKey)
        }
    }

    // Inside a modal, Dialog and Popover resolve to separate copies of Radix's dismissable-layer and
    // focus-scope, so the dialog cannot tell this picker is stacked on top of it: it would close on the
    // picker's Escape, and its focus trap would pull focus straight back out of the portaled content
    // (no typing in Custom…, no tabbing to a row). Both of Radix's copies listen on `document`, so
    // these sit one node below it and stop just the events that belong to the picker. Registered for the
    // pill's whole life rather than per-open, so they are already in place when Radix focuses the content.
    //
    // Radix re-attaches its composed ref on every commit, nulling ours in between — exactly when the
    // Custom… field autofocuses. So remember the node and ignore the nulls; a closed picker's stale
    // node is detached and contains nothing.
    const contentRef = useRef<HTMLDivElement | null>(null)
    const rememberContent = useCallback((node: HTMLDivElement | null) => {
        if (node) contentRef.current = node
    }, [])
    const closeRef = useRef<() => void>()
    closeRef.current = open ? () => changeOpen(false) : undefined
    useEffect(() => {
        const inPicker = (node: EventTarget | null) => node instanceof Node && !!contentRef.current?.contains(node)
        const onKey = (e: globalThis.KeyboardEvent) => {
            if (e.key !== 'Escape' || !closeRef.current) return
            e.stopPropagation()
            closeRef.current()
        }
        const onFocusIn = (e: FocusEvent) => {
            if (inPicker(e.target)) e.stopPropagation()
        }
        const onFocusOut = (e: FocusEvent) => {
            if (inPicker(e.target) || inPicker(e.relatedTarget)) e.stopPropagation()
        }
        const root = document.documentElement
        root.addEventListener('keydown', onKey)
        root.addEventListener('focusin', onFocusIn)
        root.addEventListener('focusout', onFocusOut)
        return () => {
            root.removeEventListener('keydown', onKey)
            root.removeEventListener('focusin', onFocusIn)
            root.removeEventListener('focusout', onFocusOut)
        }
    }, [])

    const choose = (next: string) => {
        onSelect(next)
        changeOpen(false)
    }

    const customRange = activeUnit?.custom
    const parsedCustom = customRange ? customCapacityValue(custom, customRange) : undefined
    const parsedCustomDate = customDate ? customTimeframeDate(custom) : undefined

    const commitCustom = () => {
        if (parsedCustom === undefined || !customRange) return
        choose(customRange.format(parsedCustom))
    }

    const commitCustomDate = () => {
        if (!parsedCustomDate) return
        const label = timeframeLabelForDate(parsedCustomDate)
        if (label) choose(label)
    }

    // The current pick is always visible and always checkable, even when it is off the ladder: a
    // typed 2.5, or a value saved before this ladder existed. Without this row the popover would
    // quietly drop it the moment anything else was picked.
    const ladder = activeUnit?.options ?? []
    const offLadder = Boolean(
        activeUnit && activeKey === unitKey && value && !ladder.includes(value) && !options.includes(value)
    )
    const unitRows = offLadder ? [...ladder, value] : ladder
    const plainRows = !units && value && !options.includes(value) ? [...options, value] : options

    // A plain render function, not a nested component: typing in the custom field re-renders, and a
    // fresh component type each time would remount every row mid-edit.
    const row = (option: string) => (
        <button
            key={option}
            type="button"
            onClick={() => choose(option)}
            className={cn(
                'flex w-full items-center justify-between rounded-md px-2.5 py-1.5 text-body-sm',
                'transition-colors duration-150 hover:bg-muted',
                option === value ? 'font-medium text-foreground' : 'text-secondary-foreground'
            )}
        >
            {option}
            {option === value && <Check className="size-3.5 text-primary" />}
        </button>
    )

    return (
        <Popover open={open} onOpenChange={changeOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    className={cn(
                        'inline-flex h-7 items-center gap-1.5 rounded-md border border-border bg-transparent px-2.5 text-body-sm font-medium text-foreground',
                        'transition-[background-color,color,transform] duration-150 hover:bg-muted active:scale-[0.96]',
                        'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
                        open && 'bg-muted'
                    )}
                >
                    {icon && <span className="text-muted-foreground">{icon}</span>}
                    {value}
                    <ChevronDown className="size-3 text-muted-foreground/70" />
                </button>
            </PopoverTrigger>
            <PopoverContent
                ref={rememberContent}
                align={align}
                className={cn(customDate ? 'max-h-96' : 'max-h-80', 'z-[60] w-52 overflow-y-auto p-1')}
            >
                {plainRows.map(row)}

                {customDate && !customOpen && (
                    <button
                        type="button"
                        onClick={() => {
                            setCustom(endDateForTimeframe(value) ?? '')
                            setCustomOpen(true)
                        }}
                        className="flex w-full items-center rounded-md px-2.5 py-1.5 text-body-sm text-secondary-foreground transition-colors duration-150 hover:bg-muted"
                    >
                        {CUSTOM_LABEL}
                    </button>
                )}

                {customDate && customOpen && (
                    <div className="flex items-center gap-1.5 p-1">
                        <Input
                            autoFocus
                            type="date"
                            min={DateTime.now().toISODate()}
                            value={custom}
                            onChange={(e) => setCustom(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key !== 'Enter') return
                                e.preventDefault()
                                commitCustomDate()
                            }}
                            aria-label="Custom target date"
                            className="h-8 min-w-0"
                        />
                        <button
                            type="button"
                            onClick={commitCustomDate}
                            disabled={parsedCustomDate === undefined}
                            className={cn(
                                'h-8 shrink-0 rounded-md px-2 text-body-sm font-medium transition-colors duration-150',
                                parsedCustomDate !== undefined
                                    ? 'bg-muted text-foreground hover:bg-border'
                                    : 'text-muted-foreground opacity-60'
                            )}
                        >
                            Set
                        </button>
                    </div>
                )}

                {units && activeUnit && (
                    <>
                        <div className="my-1 inline-flex w-full rounded-md border border-border p-0.5">
                            {units.map((unit) => (
                                <button
                                    key={unit.key}
                                    type="button"
                                    onClick={() => {
                                        setActiveKey(unit.key)
                                        setCustomOpen(false)
                                    }}
                                    className={cn(
                                        'flex-1 rounded-sm px-2 py-1 text-body-sm transition-colors duration-150',
                                        unit.key === activeUnit.key
                                            ? 'bg-muted font-medium text-foreground'
                                            : 'text-muted-foreground hover:text-foreground'
                                    )}
                                >
                                    {unit.label}
                                </button>
                            ))}
                        </div>

                        {unitRows.map(row)}

                        {customRange && !customOpen && (
                            <button
                                type="button"
                                onClick={() => setCustomOpen(true)}
                                className="flex w-full items-center rounded-md px-2.5 py-1.5 text-body-sm text-secondary-foreground transition-colors duration-150 hover:bg-muted"
                            >
                                {CUSTOM_LABEL}
                            </button>
                        )}

                        {customRange && customOpen && (
                            <div className="flex items-center gap-1.5 p-1">
                                <Input
                                    autoFocus
                                    type="number"
                                    inputMode="decimal"
                                    min={customRange.min}
                                    max={customRange.max}
                                    step={customRange.step}
                                    value={custom}
                                    onChange={(e) => setCustom(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key !== 'Enter') return
                                        e.preventDefault()
                                        commitCustom()
                                    }}
                                    aria-label={`Custom ${activeUnit.label.toLowerCase()} per week`}
                                    className="h-8"
                                />
                                <button
                                    type="button"
                                    onClick={commitCustom}
                                    disabled={parsedCustom === undefined}
                                    className={cn(
                                        'h-8 shrink-0 rounded-md px-2 text-body-sm font-medium transition-colors duration-150',
                                        parsedCustom !== undefined
                                            ? 'bg-muted text-foreground hover:bg-border'
                                            : 'text-muted-foreground opacity-60'
                                    )}
                                >
                                    Set
                                </button>
                            </div>
                        )}
                    </>
                )}
            </PopoverContent>
        </Popover>
    )
}

export default PropertyPill
export { PropertyPill }
